import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    IconButton,
    Link,
    Paper,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublicIcon from '@mui/icons-material/Public';
import { useSelector } from 'react-redux';
import type { RootState } from 'state/store';

import { DirectoryTree } from 'components/AssetExplorer/components';
import type {
    Asset,
    FolderTreeResponse,
} from 'components/AssetExplorer/AssetExplorer.types';
import { folderDisplayName } from 'components/AssetExplorer/AssetExplorer.types';
import {
    assetSupportsInlineImagePreview,
    buildExplorerPublishedAssetUrl,
    formatFileSize,
} from 'components/AssetExplorer/AssetExplorer.api';
import { useAssetExplorerApi } from 'components/AssetExplorer/AssetExplorerConfig';

const SEARCH_DEBOUNCE_MS = 350;

export interface PublishedAssetPickerResult {
    asset: Asset;
    /** Absolute CDN URL constructed from `assetsS3CdnUrl` + `publishedRelativeUrl`. */
    url: string;
}

export interface PublishedAssetPickerDialogProps {
    open: boolean;
    /** Optional asset type filter (e.g. 'IMAGE' or ['IMAGE', 'VIDEO']). When set, only tiles of those types are shown. */
    assetTypeFilter?: Asset['type'] | Asset['type'][];
    /** Optional title shown in dialog header. */
    title?: string;
    /** Optional label shown on the primary action button. */
    primaryActionLabel?: string;
    /** Extra controls rendered in the dialog header (e.g. a library/source switch). */
    headerActions?: React.ReactNode;
    onCancel: () => void;
    onConfirm: (result: PublishedAssetPickerResult) => void;
}

/** Asset hero icon when no inline image preview is available. */
const renderAssetHeroIcon = (type: string, size = 40) => {
    const sx = { fontSize: size, color: 'inherit' } as const;
    switch (type) {
        case 'VIDEO':
            return <VideoFileIcon sx={sx} />;
        case 'AUDIO':
            return <AudioFileIcon sx={sx} />;
        default:
            return <InsertDriveFileIcon sx={sx} />;
    }
};

/** Recursively flatten the folder tree into [id -> folder] map for breadcrumb lookups. */
function indexFolderTree(nodes: FolderTreeResponse[]): Map<string, FolderTreeResponse> {
    const map = new Map<string, FolderTreeResponse>();
    const visit = (n: FolderTreeResponse) => {
        map.set(n._id, n);
        (n.children || []).forEach(visit);
    };
    nodes.forEach(visit);
    return map;
}

/**
 * Build the ancestor chain from root → `targetId` using the in-memory folder
 * tree. Used to render breadcrumbs in the dialog without an extra round trip.
 */
function buildBreadcrumb(
    nodes: FolderTreeResponse[],
    targetId: string,
): FolderTreeResponse[] {
    const path: FolderTreeResponse[] = [];
    const walk = (list: FolderTreeResponse[]): boolean => {
        for (const n of list) {
            if (n._id === targetId) {
                path.push(n);
                return true;
            }
            if (n.children && n.children.length > 0) {
                if (walk(n.children)) {
                    path.unshift(n);
                    return true;
                }
            }
        }
        return false;
    };
    walk(nodes);
    return path;
}

interface AssetTileProps {
    asset: Asset;
    isSelected: boolean;
    onSelect: (asset: Asset) => void;
    onConfirmSelect?: (asset: Asset) => void;
}

const AssetTile: React.FC<AssetTileProps> = ({ asset, isSelected, onSelect, onConfirmSelect }) => {
    const theme = useTheme();
    const [imgBroken, setImgBroken] = useState(false);

    useEffect(() => {
        setImgBroken(false);
    }, [asset._id, asset.s3Url]);

    const showInlineImage =
        asset.type === 'IMAGE' && Boolean(asset.s3Url) && assetSupportsInlineImagePreview(asset) && !imgBroken;

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={() => onSelect(asset)}
            onDoubleClick={() => onConfirmSelect?.(asset)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    onConfirmSelect?.(asset);
                } else if (e.key === ' ') {
                    e.preventDefault();
                    onSelect(asset);
                }
            }}
            sx={{
                width: 220,
                height: 180,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                flexShrink: 0,
                userSelect: 'none',
                boxShadow: isSelected
                    ? `0 0 0 3px ${theme.palette.secondary.main}, 0 6px 20px ${alpha(theme.palette.secondary.main, 0.45)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.18)}, 0 0 0 1px ${alpha(theme.palette.success.main, 0.55)}`,
                transition: 'box-shadow 0.18s ease, transform 0.12s ease',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: isSelected
                        ? `0 0 0 3px ${theme.palette.secondary.main}, 0 8px 22px ${alpha(theme.palette.secondary.main, 0.5)}`
                        : `0 6px 16px ${alpha(theme.palette.common.black, 0.24)}, 0 0 0 1px ${alpha(theme.palette.success.main, 0.75)}`,
                },
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.secondary.main}`,
                    outlineOffset: 2,
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    bottom: 56,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {showInlineImage ? (
                    <Box
                        component="img"
                        src={asset.s3Url}
                        alt={asset.name}
                        onError={() => setImgBroken(true)}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Box sx={{ color: 'rgba(255,255,255,0.9)' }}>{renderAssetHeroIcon(asset.type, 44)}</Box>
                )}

                <Chip
                    label={asset.type}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: '#fff',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                    }}
                />

                <Tooltip title="Published">
                    <PublicIcon
                        sx={{
                            position: 'absolute',
                            top: 6,
                            right: 6,
                            fontSize: 18,
                            color: theme.palette.success.light,
                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
                        }}
                    />
                </Tooltip>

                {isSelected && (
                    <Checkbox
                        checked
                        readOnly
                        size="small"
                        sx={{
                            position: 'absolute',
                            bottom: 6,
                            right: 6,
                            p: 0.25,
                            bgcolor: 'rgba(255,255,255,0.92)',
                            borderRadius: 1,
                            '&.Mui-checked': { color: theme.palette.secondary.dark },
                        }}
                    />
                )}
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 56,
                    px: 1.25,
                    py: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}
                    title={asset.name}
                >
                    {asset.name}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}
                >
                    {formatFileSize(asset.size)}
                </Typography>
            </Box>
        </Box>
    );
};

interface FolderTileProps {
    folder: FolderTreeResponse;
    disabled: boolean;
    onOpen: (folder: FolderTreeResponse) => void;
}

const FolderTile: React.FC<FolderTileProps> = ({ folder, disabled, onOpen }) => {
    const theme = useTheme();
    return (
        <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={() => {
                if (!disabled) onOpen(folder);
            }}
            onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(folder);
                }
            }}
            sx={{
                width: 220,
                height: 180,
                borderRadius: 2.5,
                flexShrink: 0,
                position: 'relative',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                border: `2px dashed ${alpha(theme.palette.secondary.main, 0.7)}`,
                background: `linear-gradient(155deg, ${alpha(theme.palette.secondary.main, 0.14)} 0%, ${alpha(theme.palette.secondary.dark, 0.06)} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                px: 2,
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
                '&:hover': disabled
                    ? undefined
                    : {
                          borderColor: theme.palette.secondary.main,
                          boxShadow: `0 6px 18px ${alpha(theme.palette.secondary.main, 0.22)}`,
                          transform: 'translateY(-2px)',
                      },
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.secondary.main}`,
                    outlineOffset: 2,
                },
            }}
        >
            <FolderOutlinedIcon sx={{ fontSize: 64, color: 'secondary.main' }} />
            <Typography
                variant="subtitle1"
                noWrap
                sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    color: theme.palette.mode === 'light'
                        ? alpha(theme.palette.grey[900], 0.94)
                        : theme.palette.secondary.dark,
                }}
                title={folderDisplayName(folder)}
            >
                {folderDisplayName(folder)}
            </Typography>
            {disabled && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    No published assets
                </Typography>
            )}
        </Box>
    );
};

export const PublishedAssetPickerDialog: React.FC<PublishedAssetPickerDialogProps> = ({
    open,
    assetTypeFilter,
    title = 'Select Published Asset',
    primaryActionLabel = 'Use Asset',
    headerActions,
    onCancel,
    onConfirm,
}) => {
    const theme = useTheme();
    // Bound to the surrounding <AssetExplorerConfigProvider> (defaults to Asset Management `/ae`).
    const { getAssets, getChildFolders, getFolderTree, getFoldersWithPublishedAssets, searchAssets, basePath } = useAssetExplorerApi();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const assetsS3CdnUrl = useSelector(
        (state: RootState) => state.clientConfig.data?.assetsS3CdnUrl,
    );

    const [folders, setFolders] = useState<FolderTreeResponse[]>([]);
    const [foldersWithPublishedSet, setFoldersWithPublishedSet] = useState<Set<string>>(new Set());
    const [selectedFolder, setSelectedFolder] = useState<FolderTreeResponse | null>(null);
    const [childFolders, setChildFolders] = useState<FolderTreeResponse[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<FolderTreeResponse[]>([]);

    const [assets, setAssets] = useState<Asset[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(24);
    const [totalAssets, setTotalAssets] = useState(0);

    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<Asset[]>([]);

    const [treeLoading, setTreeLoading] = useState(false);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const searchDebounceRef = useRef<number | null>(null);

    const folderIndex = useMemo(() => indexFolderTree(folders), [folders]);

    /** A folder can be opened when it holds a published asset, or when it is a company-shared published folder. */
    const allowedFolderIds = useMemo(() => {
        const allowed = new Set<string>();
        const visit = (node: FolderTreeResponse, insideShared: boolean): boolean => {
            const opensShare = Boolean((node.shared || node.sharedWithCompany) && node.published !== false);
            const scope = insideShared || opensShare;
            let anyDescendant = false;
            (node.children || []).forEach((child) => {
                if (visit(child, scope)) anyDescendant = true;
            });
            const self = foldersWithPublishedSet.has(node._id);
            if (self || anyDescendant || scope) {
                allowed.add(node._id);
                return true;
            }
            return false;
        };
        folders.forEach((node) => visit(node, false));
        return allowed;
    }, [folders, foldersWithPublishedSet]);

    const disabledFolderIds = useMemo(() => {
        const disabled = new Set<string>();
        folderIndex.forEach((_node, id) => {
            if (!allowedFolderIds.has(id)) disabled.add(id);
        });
        return disabled;
    }, [folderIndex, allowedFolderIds]);

    const loadFolderTree = useCallback(async () => {
        setTreeLoading(true);
        setErrorMsg(null);
        try {
            const [tree, publishedFolderIds] = await Promise.all([
                getFolderTree(),
                getFoldersWithPublishedAssets(),
            ]);
            setFolders(tree);
            setFoldersWithPublishedSet(new Set(publishedFolderIds));

            const root = tree[0] || null;
            setSelectedFolder(root);
            setBreadcrumb(root ? [root] : []);
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to load asset folders');
        } finally {
            setTreeLoading(false);
        }
    }, [getFolderTree, getFoldersWithPublishedAssets]);

    const loadFolderContents = useCallback(async () => {
        if (!selectedFolder) return;
        setAssetsLoading(true);
        setErrorMsg(null);
        try {
            const [kids, response] = await Promise.all([
                getChildFolders(selectedFolder._id),
                getAssets(selectedFolder._id, page, rowsPerPage, 'createdAt', 'desc', true),
            ]);
            setChildFolders(kids);
            setAssets(response.assets);
            setTotalAssets(response.pagination.total);
        } catch (err: any) {
            setErrorMsg(err?.message || 'Failed to load folder contents');
            setAssets([]);
            setChildFolders([]);
            setTotalAssets(0);
        } finally {
            setAssetsLoading(false);
        }
    }, [selectedFolder, page, rowsPerPage, getChildFolders, getAssets]);

    useEffect(() => {
        if (!open) return;

        setSelectedAsset(null);
        setSearchInput('');
        setSearchQuery('');
        setSearchResults([]);
        setPage(0);
        setMobileDrawerOpen(false);
        setSelectedFolder(null);
        setChildFolders([]);
        setAssets([]);
        loadFolderTree();
    }, [open, loadFolderTree, basePath]);

    useEffect(() => {
        if (!open) return;
        if (searchQuery.trim().length > 0) return;
        loadFolderContents();
    }, [open, searchQuery, loadFolderContents]);

    useEffect(() => {
        if (searchDebounceRef.current) {
            window.clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = window.setTimeout(() => {
            setSearchQuery(searchInput.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            if (searchDebounceRef.current) {
                window.clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = null;
            }
        };
    }, [searchInput]);

    useEffect(() => {
        if (!open) return;
        const q = searchQuery.trim();
        if (q.length === 0) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }
        let cancelled = false;
        setIsSearching(true);
        searchAssets(q, true)
            .then((list) => {
                if (cancelled) return;
                setSearchResults(list);
            })
            .catch((err) => {
                if (cancelled) return;
                setErrorMsg(err?.message || 'Search failed');
                setSearchResults([]);
            })
            .finally(() => {
                if (cancelled) return;
                setIsSearching(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, searchQuery, searchAssets]);

    const handleFolderSelect = (folder: FolderTreeResponse) => {
        setSelectedFolder(folder);
        setPage(0);
        setBreadcrumb(buildBreadcrumb(folders, folder._id));
        setSelectedAsset(null);
        if (isMobile) setMobileDrawerOpen(false);
    };

    const handleOpenChildFolder = (folder: FolderTreeResponse) => {
        const fromTree = folderIndex.get(folder._id) || folder;
        handleFolderSelect(fromTree);
    };

    const handleAssetTileSelect = (asset: Asset) => {
        setSelectedAsset((prev) => (prev?._id === asset._id ? null : asset));
    };

    const confirmAsset = useCallback(
        (asset: Asset) => {
            const url = buildExplorerPublishedAssetUrl(assetsS3CdnUrl, asset.publishedRelativeUrl);
            if (!url) {
                setErrorMsg(
                    'Cannot construct CDN URL for this asset. Verify that the Assets S3 CDN URL is configured for this domain.',
                );
                return;
            }
            onConfirm({ asset, url });
        },
        [assetsS3CdnUrl, onConfirm],
    );

    const handleConfirmClick = () => {
        if (selectedAsset) confirmAsset(selectedAsset);
    };

    const isInSearchMode = searchQuery.trim().length > 0;

    /** Hide tiles that don't match the optional `assetTypeFilter`. */
    const displayedAssets = useMemo(() => {
        const source = isInSearchMode ? searchResults : assets;
        if (!assetTypeFilter) return source;
        const allowed = new Set(Array.isArray(assetTypeFilter) ? assetTypeFilter : [assetTypeFilter]);
        return source.filter((a) => allowed.has(a.type));
    }, [assets, searchResults, isInSearchMode, assetTypeFilter]);

    const displayedChildFolders = useMemo(() => {
        if (isInSearchMode) return [];
        return childFolders;
    }, [childFolders, isInSearchMode]);

    const folderColumn = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <FolderIcon fontSize="small" color="secondary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Content Tree
                </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <DirectoryTree
                    folders={folders}
                    selectedFolderId={selectedFolder?._id || null}
                    onFolderSelect={handleFolderSelect}
                    onFolderCreate={() => undefined}
                    onFolderDelete={() => undefined}
                    loading={treeLoading}
                    pickerMode
                    disabledFolderIds={disabledFolderIds}
                />
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            fullWidth
            fullScreen={isMobile}
            maxWidth="lg"
            PaperProps={{
                sx: {
                    height: { xs: '100%', md: '88vh' },
                    maxHeight: { xs: '100%', md: '88vh' },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    pr: 1,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <PublicIcon color="success" />
                    <Typography variant="h6" component="span" sx={{ fontWeight: 700 }} noWrap>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {headerActions}
                    <IconButton onClick={onCancel} size="small" aria-label="Close picker">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            width: 280,
                            flexShrink: 0,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderRadius: 0,
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {folderColumn}
                    </Paper>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box
                            sx={{
                                px: { xs: 1.5, md: 2 },
                                py: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    startIcon={<AccountTreeIcon />}
                                    onClick={() => setMobileDrawerOpen(true)}
                                    sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                                >
                                    Browse
                                </Button>

                                <Breadcrumbs sx={{ flex: 1, minWidth: 0 }}>
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => folders[0] && handleFolderSelect(folders[0])}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            textDecoration: 'none',
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                        My Assets
                                    </Link>
                                    {breadcrumb.slice(1).map((folder, index) => (
                                        <Link
                                            key={folder._id}
                                            component="button"
                                            variant="body2"
                                            onClick={() => handleFolderSelect(folder)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                textDecoration: 'none',
                                                color:
                                                    index === breadcrumb.length - 2
                                                        ? theme.palette.text.primary
                                                        : theme.palette.text.secondary,
                                                fontWeight: index === breadcrumb.length - 2 ? 600 : 400,
                                            }}
                                        >
                                            <FolderIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                            {folderDisplayName(folder)}
                                        </Link>
                                    ))}
                                </Breadcrumbs>

                                <Tooltip title="Refresh">
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => {
                                            loadFolderTree();
                                            if (!isInSearchMode) loadFolderContents();
                                        }}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'secondary.main',
                                        }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <TextField
                                label="Search published assets"
                                size="small"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                color="secondary"
                                placeholder="Search by name across all your folders…"
                                fullWidth
                                helperText={
                                    isInSearchMode
                                        ? isSearching
                                            ? 'Searching…'
                                            : `${searchResults.length} match${searchResults.length === 1 ? '' : 'es'} (server-side, across all folders)`
                                        : 'Results are restricted to published assets only.'
                                }
                            />
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                p: { xs: 1.5, md: 2 },
                                position: 'relative',
                            }}
                        >
                            {(assetsLoading && !isInSearchMode) || (isSearching && isInSearchMode) ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: 200,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {errorMsg && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 1.5, color: 'error.main' }}
                                        >
                                            {errorMsg}
                                        </Typography>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 2,
                                            alignContent: 'flex-start',
                                        }}
                                    >
                                        {displayedChildFolders.map((folder) => {
                                            const disabled = !allowedFolderIds.has(folder._id);
                                            return (
                                                <FolderTile
                                                    key={folder._id}
                                                    folder={folder}
                                                    disabled={disabled}
                                                    onOpen={handleOpenChildFolder}
                                                />
                                            );
                                        })}
                                        {displayedAssets.map((asset) => (
                                            <AssetTile
                                                key={asset._id}
                                                asset={asset}
                                                isSelected={selectedAsset?._id === asset._id}
                                                onSelect={handleAssetTileSelect}
                                                onConfirmSelect={confirmAsset}
                                            />
                                        ))}
                                    </Box>
                                    {!isInSearchMode &&
                                        displayedChildFolders.length === 0 &&
                                        displayedAssets.length === 0 && (
                                            <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                                                <Typography variant="body2">
                                                    No published assets in this folder.
                                                </Typography>
                                            </Box>
                                        )}
                                    {isInSearchMode && displayedAssets.length === 0 && !isSearching && (
                                        <Box sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>
                                            <Typography variant="body2">
                                                No published assets match “{searchQuery}”.
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>

                        {!isInSearchMode && (
                            <Box
                                sx={{
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    px: 1,
                                    py: 0.25,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                }}
                            >
                                <TablePagination
                                    component="div"
                                    count={totalAssets}
                                    page={page}
                                    onPageChange={(_, p) => setPage(p)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[12, 24, 48, 96]}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                    {selectedAsset ? (
                        <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
                            Selected:{' '}
                            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                {selectedAsset.name}
                            </Box>
                        </Typography>
                    ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Click a tile to select. Double-click (or Enter) to insert immediately.
                        </Typography>
                    )}
                </Box>
                <Button color="secondary" variant="outlined" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleConfirmClick}
                    disabled={!selectedAsset}
                >
                    {primaryActionLabel}
                </Button>
            </DialogActions>

            <Drawer
                anchor="left"
                open={mobileDrawerOpen}
                onClose={() => setMobileDrawerOpen(false)}
                sx={{ display: { xs: 'block', md: 'none' } }}
                PaperProps={{ sx: { width: '85%', maxWidth: 320 } }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        px: 1,
                        py: 0.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>{folderColumn}</Box>
            </Drawer>
        </Dialog>
    );
};

export default PublishedAssetPickerDialog;
